import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useMutateRenameOrganization } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';

interface IRenameProps {
  name: string;
}

interface IModalProps {
  currentOrgName: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('New workspace name is required'),
});

export const RenameOrganizationModal = ({ currentOrgName }: IModalProps) => {
  const { lang } = useTranslation();
  const defaultValues = {
    name: currentOrgName,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const [openModal, setOpenModal] = useState(false);
  const { organization_id } = useParams();

  const { mutateAsync } = useMutateRenameOrganization();

  const onSubmit: SubmitHandler<IRenameProps> = async (data) => {
    try {
      await mutateAsync({ formData: data, organization_id });
      reset();
      setOpenModal(false);
      toast.success(lang.get('msg.organizationSuccessfullyRendamed'));
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  return (
    <AlertDialog open={openModal} onOpenChange={setOpenModal}>
      <AlertDialogTrigger asChild>
        <p className="py-1 pl-2 pr-10 text-sm cursor-pointer hover:bg-secondary-background">
          {lang.get('msg.rename')}
        </p>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-wrap items-center gap-1">
              {lang.get('msg.renameOrganization')}
            </AlertDialogTitle>
            <div className="flex flex-col pt-5 pb-12">
              <label className="relative flex flex-col w-full gap-1">
                <Input
                  {...register('name')}
                  placeholder="Enter new workspace name"
                  className={`w-full ${errors?.name?.message && 'border-red-600/50'}`}
                />
                <p
                  className={`text-xs md700:hidden font-medium text-red-600 opacity-0 absolute -bottom-5 ${
                    errors?.name?.message && 'opacity-100'
                  }`}
                >
                  {errors?.name?.message}
                </p>
              </label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button variant="default" type="submit" disabled={isSubmitting}>
              {lang.get('msg.submit')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}